/**
 * Editorial content for the easement valuation calculator: methodology prose,
 * per-easement-type notes, FAQ entries and the JSON-LD builders. Kept out of
 * the route file so the copy can be unit tested.
 */
import { BASE_URL } from "@/lib/seo-routes";
import { EASEMENT_TYPES, type EasementType } from "@/lib/easement-valuation";

export const CALCULATOR_PATH = "/easement-value-calculator";
export const CALCULATOR_URL = `${BASE_URL}${CALCULATOR_PATH}`;

export const CALCULATOR_TITLE =
  "Easement Value Calculator: Property Value Impact | Easements.com";

export const CALCULATOR_DESCRIPTION =
  "Estimate how much a utility, pipeline, road or access easement lowers property value, then compare your range with dollar awards from real court cases.";

export const CALCULATOR_PLANNING_NOTE =
  "This calculator produces a preliminary planning range, not an appraisal. Easement damages depend on local market evidence, the exact recorded language, and the improvements involved. Have a licensed appraiser, surveyor, or real estate attorney review anything you rely on for a purchase, a condemnation claim, or a construction decision.";

export interface MethodologyStep {
  heading: string;
  body: string;
}

export const METHODOLOGY_STEPS: MethodologyStep[] = [
  {
    heading: "Before-and-after (larger parcel) framework",
    body: "Appraisers value a partial acquisition by appraising the whole property before the easement, then appraising it again subject to the easement. The difference is the compensable impact. This calculator reproduces that structure with two components you can inspect separately: the value lost inside the corridor, and the damage to the remainder of the parcel.",
  },
  {
    heading: "Corridor value uses a percentage of fee, not the full fee",
    body: "An easement is not a sale. You keep title, you keep the property tax bill, and you usually keep some use of the strip. Market practice is to pay a percentage of the underlying land value for the encumbered area. Commonly a third for a buried utility line that leaves the surface usable, and near the full fee where the holder gets exclusive, no-build control. The model applies a per-type band and then shifts it up or down based on the restriction level you choose.",
  },
  {
    heading: "Damage to the remainder is where the real money hides",
    body: "A ten-foot utility strip along a back fence rarely changes what a buyer will pay. The same ten feet through the only spot the house fits can cost far more than the strip itself. The model adds remainder damage when the corridor crosses the buildable area, affects the only legal access, or splits the parcel, and reduces it when the corridor hugs an existing edge.",
  },
  {
    heading: "Land value is separated from improvements",
    body: "Easement damage attaches primarily to land and to what you can build on it, so the model works from the land component of value. The land share defaults to a typical suburban split; raise it for rural acreage where land is most of the value, and lower it for a small lot with an expensive house on it.",
  },
  {
    heading: "Why the range is wide",
    body: "Published easement settlements for similar corridors routinely vary by a factor of two or more between markets. A wide range is honest. Treat the low end as the outcome where the corridor is a nuisance only, the high end as the outcome where it genuinely changes what the property can become, and the midpoint as the starting point for a conversation with an appraiser.",
  },
];

export const TYPE_NOTES: Record<EasementType, string> = {
  utility:
    "Buried water, sewer, electric, and communication lines are the most common and usually the cheapest kind of encumbrance. You keep lawn, garden, and often parking use; you lose the right to build structures or plant deep-rooted trees over the line, and the holder can excavate to repair it.",
  transmission:
    "Overhead transmission corridors carry the widest documented value effects: the strip itself is close to unusable, the towers and conductors are visible from the whole property, and buyer resistance extends well beyond the corridor. This is the type where remainder damage most often exceeds corridor value.",
  pipeline:
    "Pipeline easements combine a no-build strip with a permanent setback, disclosure obligations, and buyer perception risk. The corridor is typically valued near the top of the band, and remainder damage depends heavily on whether the route crosses the buildable envelope.",
  access:
    "An ingress-egress or shared driveway easement burdens the strip and adds traffic, maintenance disputes, and privacy loss. If it is the only legal access for a neighboring parcel, expect meaningful remainder damage; if it duplicates an existing drive, expect very little.",
  drainage:
    "Drainage and stormwater easements often follow ground that was never buildable anyway, which keeps the impact modest. The exception is when the easement documents a flow path across usable ground or brings a maintenance obligation with it.",
  conservation:
    "Conservation easements permanently extinguish development rights, so the corridor is valued near the full fee. Their value question is usually the reverse of the others: the owner is measuring the donation or tax benefit against the development value given up.",
  shared:
    "Shared use and prescriptive claims are the least certain category, because the scope may never have been written down. The corridor discount is lower than a recorded exclusive easement, but the uncertainty itself can affect financing and marketability until it is resolved or quieted.",
};

export interface CalculatorFaq {
  question: string;
  answer: string;
}

export const CALCULATOR_FAQS: CalculatorFaq[] = [
  {
    question: "How much does an easement reduce property value?",
    answer:
      "There is no single number. In practice the impact ranges from effectively zero for a narrow buried utility line along a rear boundary to 20% or more of total value when a wide, no-build corridor crosses the buildable area or controls the only access. The two things that drive the answer are how much land the corridor covers and whether it takes away something you would otherwise have built.",
  },
  {
    question: "How is easement compensation normally calculated?",
    answer:
      "By the before-and-after method: value the property without the easement, value it again subject to the easement, and pay the difference. That difference is usually presented as a percentage of the land value inside the corridor plus any measurable damage to the remaining land.",
  },
  {
    question: "Why does an easement cost less than buying the land outright?",
    answer:
      "Because it is not a sale. You keep ownership, and in most cases you keep some use of the surface. Mowing, gardening, parking, or crossing. The percentage paid reflects how much of the bundle of rights the holder takes, which is why an exclusive no-build corridor is worth far more than a buried line you can still grow grass over.",
  },
  {
    question: "Does an existing easement lower the price a buyer should pay?",
    answer:
      "It can, but only to the extent it changes what the buyer can do. Run the numbers for the corridor as it actually exists, then compare that figure with the cost of the plan the buyer is giving up. If the easement blocks nothing they intended to build, the practical discount is small.",
  },
  {
    question: "Can I use this calculator for a condemnation or utility offer?",
    answer:
      "Use it to sanity-check an offer before you respond, not to replace an appraisal. If the numbers here are far from what you were offered, that gap is the reason to hire an appraiser who works in your county and can support the figure with local sales.",
  },
  {
    question: "What if I do not know the corridor width or length?",
    answer:
      "Recorded easements almost always state a width: 10, 15, 20, or 30 feet is typical for utilities, and 50 feet or more for transmission and pipeline corridors. If you have a title commitment, the width is usually in the Schedule B exception, and you can upload the document to have those exceptions translated into plain English.",
  },
];

export function calculatorFaqJsonLd(): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: CALCULATOR_FAQS.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  });
}

export function calculatorAppJsonLd(): string {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Easement & Right-of-Way Value Impact Calculator",
    url: CALCULATOR_URL,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Any (web browser)",
    description: CALCULATOR_DESCRIPTION,
    isAccessibleForFree: true,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    featureList: EASEMENT_TYPES.map((t) => t.label),
    publisher: {
      "@type": "Organization",
      name: "Easements.com",
      url: `${BASE_URL}/`,
    },
  });
}

/** Attribution block writers can copy when they reference the tool. */
export const CITATION_TEXT = `Easements.com, "Easement & Right-of-Way Value Impact Calculator," ${CALCULATOR_URL}`;

export const CITATION_HTML = `<a href="${CALCULATOR_URL}">Easement &amp; Right-of-Way Value Impact Calculator</a>: Easements.com`;
